import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, AlertTriangle, Ban, CheckCircle, Search, RefreshCw } from 'lucide-react';


const SecurityMonitoring = () => {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [blockedIps, setBlockedIps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchIP, setSearchIP] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [ipDetails, setIpDetails] = useState(null);
  const [emailDetails, setEmailDetails] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, logsRes, blockedRes] = await Promise.all([
        axios.get(`/api/security/stats`),
        axios.get(`/api/security/logs?limit=20`),
        axios.get(`/api/security/blocked-ips`)
      ]);

      setStats(statsRes.data);
      setLogs(logsRes.data.logs);
      setBlockedIps(blockedRes.data.blocked_ips);
    } catch (error) {
      console.error('Error fetching security data:', error);
      toast.error('Failed to load security data');
    } finally {
      setLoading(false);
    }
  };

  const searchIPAddress = async () => {
    if (!searchIP) return;
    
    try {
      const response = await axios.get(`/api/security/ip/${searchIP}`);
      setIpDetails(response.data);
      toast.success('IP details loaded');
    } catch (error) {
      toast.error('Failed to load IP details');
    }
  };

  const searchEmailAddress = async () => {
    if (!searchEmail) return;
    
    try {
      const response = await axios.get(`/api/security/user/${searchEmail}`);
      setEmailDetails(response.data);
      toast.success('User details loaded');
    } catch (error) {
      toast.error('Failed to load user details');
    }
  };

  const blockIP = async (ip, reason) => {
    try {
      await axios.post(`/api/security/block-ip`, {
        ip_address: ip,
        reason: reason || 'Suspicious activity',
        blocked_by: 'admin'
      });
      
      toast.success(`IP ${ip} has been blocked`);
      fetchData();
    } catch (error) {
      toast.error('Failed to block IP');
    }
  };

  const unblockIP = async (ip) => {
    try {
      await axios.post(`/api/security/unblock-ip`, {
        ip_address: ip
      });
      
      toast.success(`IP ${ip} has been unblocked`);
      fetchData();
    } catch (error) {
      toast.error('Failed to unblock IP');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="text-red-400" size={32} />
          <div>
            <h2 className="text-2xl font-bold text-white">Security Monitoring</h2>
            <p className="text-gray-400 text-sm">Track and prevent fraudulent payment activity</p>
          </div>
        </div>
        <button
          onClick={fetchData}
          className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-blue-900/40 to-blue-800/40 border border-blue-500/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-300 text-sm">Total Attempts</p>
                <p className="text-white text-2xl font-bold">{stats.total_attempts}</p>
              </div>
              <CheckCircle className="text-blue-400" size={32} />
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-900/40 to-green-800/40 border border-green-500/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-300 text-sm">Successful</p>
                <p className="text-white text-2xl font-bold">{stats.successful_payments}</p>
              </div>
              <CheckCircle className="text-green-400" size={32} />
            </div>
          </div>

          <div className="bg-gradient-to-br from-red-900/40 to-red-800/40 border border-red-500/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-300 text-sm">Suspicious</p>
                <p className="text-white text-2xl font-bold">{stats.suspicious_attempts}</p>
              </div>
              <AlertTriangle className="text-red-400" size={32} />
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-900/40 to-orange-800/40 border border-orange-500/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-300 text-sm">Blocked IPs</p>
                <p className="text-white text-2xl font-bold">{stats.blocked_ips}</p>
              </div>
              <Ban className="text-orange-400" size={32} />
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700">
        {['overview', 'search', 'blocked'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 font-semibold capitalize transition-colors ${
              activeTab === tab
                ? 'text-purple-400 border-b-2 border-purple-400'
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          <h3 className="text-xl font-bold text-white">Recent Activity</h3>
          <div className="bg-gray-900/50 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-gray-300 text-sm">Time</th>
                  <th className="px-4 py-3 text-left text-gray-300 text-sm">Event</th>
                  <th className="px-4 py-3 text-left text-gray-300 text-sm">Email</th>
                  <th className="px-4 py-3 text-left text-gray-300 text-sm">IP</th>
                  <th className="px-4 py-3 text-left text-gray-300 text-sm">Amount</th>
                  <th className="px-4 py-3 text-left text-gray-300 text-sm">Status</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, idx) => (
                  <tr key={idx} className="border-t border-gray-800 hover:bg-gray-800/50">
                    <td className="px-4 py-3 text-gray-400 text-sm">
                      {new Date(log.timestamp).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-gray-300 text-sm">{log.event_type}</td>
                    <td className="px-4 py-3 text-gray-300 text-sm">{log.user_email || 'N/A'}</td>
                    <td className="px-4 py-3 text-gray-300 text-sm font-mono">{log.ip_address || 'N/A'}</td>
                    <td className="px-4 py-3 text-gray-300 text-sm">
                      ${log.payment_data?.amount || 0}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded text-xs font-semibold ${
                          log.suspicious
                            ? 'bg-red-900/50 text-red-300'
                            : log.status === 'success'
                            ? 'bg-green-900/50 text-green-300'
                            : log.status === 'failed'
                            ? 'bg-orange-900/50 text-orange-300'
                            : 'bg-gray-700 text-gray-300'
                        }`}
                      >
                        {log.suspicious ? '🚨 Suspicious' : log.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Search Tab */}
      {activeTab === 'search' && (
        <div className="space-y-6">
          {/* IP Search */}
          <div className="bg-gray-900/50 rounded-lg p-6">
            <h3 className="text-lg font-bold text-white mb-4">Search by IP Address</h3>
            <div className="flex gap-2">
              <input
                type="text"
                value={searchIP}
                onChange={(e) => setSearchIP(e.target.value)}
                placeholder="Enter IP address (e.g., 192.168.1.1)"
                className="flex-1 bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-purple-500 focus:outline-none"
              />
              <button
                onClick={searchIPAddress}
                className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <Search size={16} />
                Search
              </button>
            </div>

            {ipDetails && (
              <div className="mt-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-800 p-4 rounded-lg">
                    <p className="text-gray-400 text-sm">Total Attempts</p>
                    <p className="text-white text-2xl font-bold">{ipDetails.statistics.total_attempts}</p>
                  </div>
                  <div className="bg-gray-800 p-4 rounded-lg">
                    <p className="text-gray-400 text-sm">Suspicious</p>
                    <p className="text-red-400 text-2xl font-bold">{ipDetails.statistics.suspicious_attempts}</p>
                  </div>
                </div>

                {ipDetails.is_blocked ? (
                  <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-4">
                    <p className="text-red-300 font-semibold">⛔ This IP is currently blocked</p>
                    <p className="text-red-400 text-sm mt-1">Reason: {ipDetails.block_reason}</p>
                    <button
                      onClick={() => unblockIP(searchIP)}
                      className="mt-3 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                    >
                      Unblock IP
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => blockIP(searchIP, 'Manually blocked by admin')}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    Block This IP
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Email Search */}
          <div className="bg-gray-900/50 rounded-lg p-6">
            <h3 className="text-lg font-bold text-white mb-4">Search by Email</h3>
            <div className="flex gap-2">
              <input
                type="email"
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                placeholder="Enter email address"
                className="flex-1 bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-purple-500 focus:outline-none"
              />
              <button
                onClick={searchEmailAddress}
                className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <Search size={16} />
                Search
              </button>
            </div>

            {emailDetails && (
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div className="bg-gray-800 p-4 rounded-lg">
                  <p className="text-gray-400 text-sm">Total Attempts</p>
                  <p className="text-white text-2xl font-bold">{emailDetails.statistics.total_attempts}</p>
                </div>
                <div className="bg-gray-800 p-4 rounded-lg">
                  <p className="text-gray-400 text-sm">Unique IPs</p>
                  <p className="text-white text-2xl font-bold">{emailDetails.statistics.unique_ips}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Blocked IPs Tab */}
      {activeTab === 'blocked' && (
        <div className="bg-gray-900/50 rounded-lg p-6">
          <h3 className="text-lg font-bold text-white mb-4">Blocked IP Addresses</h3>
          {blockedIps.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No blocked IPs</p>
          ) : (
            <div className="space-y-3">
              {blockedIps.map((blocked, idx) => (
                <div key={idx} className="bg-gray-800 rounded-lg p-4 flex items-center justify-between">
                  <div>
                    <p className="text-white font-mono font-semibold">{blocked.ip_address}</p>
                    <p className="text-gray-400 text-sm">Reason: {blocked.reason}</p>
                    <p className="text-gray-500 text-xs">
                      Blocked: {new Date(blocked.blocked_at).toLocaleString()}
                    </p>
                  </div>
                  <button
                    onClick={() => unblockIP(blocked.ip_address)}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                  >
                    Unblock
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SecurityMonitoring;
